import {useMutation, useQueryClient} from '@github-ui/react-query'
import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {taskApiPath, taskByIdApiPath} from '../utils/api-url-helpers'
import {isDefaultOriginAgentTaskQueryKey} from './use-task'
import {agentTasksQueryKey} from './useTasks'
import type {HydratedAPITask} from '../types/api-task'
import {updateTaskInPage, type TaskListPage} from './repo-task-utils'
import {agentTaskPinsQueryKey} from '../utils/task-pins'

type TaskSharingStatus = 'unshared' | 'shared'

interface UseUpdateTaskSharingStatusArgs {
  owner?: string
  repo?: string
  taskId?: string
}

interface UpdateTaskSharingStatusVariables {
  sharing_status: TaskSharingStatus
  /**
   * Updates this task instead of the hook's. The mutation reads the hook's
   * latest arguments when it is called, so a caller whose task can change while
   * it awaits other work names the task it started with.
   */
  taskId?: string
}

export function useUpdateTaskSharingStatus({owner, repo, taskId: hookTaskId}: UseUpdateTaskSharingStatusArgs) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['updateTaskSharingStatus', owner, repo, hookTaskId],
    mutationFn: async ({sharing_status, taskId = hookTaskId}: UpdateTaskSharingStatusVariables) => {
      if (!taskId) {
        throw new Error('taskId is required')
      }

      const path = owner && repo ? taskApiPath(owner, repo, taskId) : taskByIdApiPath(taskId)
      const response = await fetchCAPI({
        path,
        method: 'PATCH',
        body: {sharing_status},
      })
      return response.json()
    },
    onSuccess: (_data, {sharing_status, taskId = hookTaskId}) => {
      if (!taskId) return

      void queryClient.invalidateQueries({queryKey: agentTaskPinsQueryKey})
      // One task is cached under several owner/repo key variants (the task view
      // scopes its key; the share command does not), so match on the task id and
      // leave owner/repo open. This request uses the default CAPI origin, so keys
      // for a custom API URL are left alone. `updateTaskInPage` patches only the
      // matching task, so the broader list match cannot touch a neighbouring one.
      queryClient.setQueriesData<HydratedAPITask>(
        {predicate: query => isDefaultOriginAgentTaskQueryKey(query.queryKey, taskId)},
        oldData => {
          if (!oldData) return oldData
          return {...oldData, sharing_status}
        },
      )

      queryClient.setQueriesData<TaskListPage>({queryKey: ['repo-tasks']}, oldData =>
        updateTaskInPage(oldData, taskId, {sharing_status}),
      )

      queryClient.setQueriesData<TaskListPage>({queryKey: agentTasksQueryKey}, oldData =>
        updateTaskInPage(oldData, taskId, {sharing_status}),
      )
    },
  })
}
